import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import * as types from 'src/types';
import { Alert, Box, Drawer, Snackbar, Typography, MenuItem, Select, FormControl, InputLabel, OutlinedInput, TextField } from '@mui/material/';
import Link from 'next/link';
import { issueTypes, getDescription, PayerPersonalInfoRequest, issueType } from 'src/helpers/issueType';
import { issueRole } from 'src/helpers/issueRole';
import { loadOpenClaims } from 'src/api/claimApi';
import { createIssue } from 'src/api/issueApi';
import MultiSelectClaims from 'src/components/FormElements/MultiSelectClaims';
import issueStyles from 'styles/issues.module.css';
import MessageForm from './components/MessageForm';

const initialState = {
  message: {
    text: '',
    attachments: [],
  },
  // Payment arrangement fields shown for L2 (PayerAgreementRequest) claims
  paymentAmount: '',
  monthlyCapacity: '',
  installments: '',
  availableClaims: [],
  isSubmittingIssue: false,
  areAttachmentsValid: true,
  error: null,
  snackbar: { open: false, message: '', severity: 'success' },
};

class CreateIssueSideBar extends PureComponent {
  static propTypes = {
    closeCreateIssueDrawer: PropTypes.func.isRequired,
    issuesChangedCallback: PropTypes.func,
    claims: PropTypes.arrayOf(types.claim),
    preSelectedIssueType: PropTypes.string,
    hasContactInfo: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    claims: [],
    preSelectedIssueType: null,
    issuesChangedCallback: () => {}, // do nothing
  };

  constructor(props) {
    super(props);
    const isPersonalInfoRequest = this.props.preSelectedIssueType === PayerPersonalInfoRequest;

    this.state = {
      ...initialState,
      type: props.preSelectedIssueType ? props.preSelectedIssueType : '',
      isPersonalInfoRequest,
      claimIds: props.claims.map((x) => x.claimId),
      hasContactInfo: props.hasContactInfo,
      message: {
        text: isPersonalInfoRequest ? 'Vinsamlegast sendið mér þær persónuupplýsingar sem þið hafið um mig.' : '',
        attachments: [],
        pendingResponseFrom: isPersonalInfoRequest ? issueRole.Personuvernd : issueRole.Greidendathjonusta,
      },
    };
  }

  componentDidMount() {
    loadOpenClaims(
      () => {},
      (data) => {
        this.setState({
          availableClaims: data.claims.filter(
            (x) =>
              !this.props.claims ||
              this.props.claims.length === 0 ||
              x.claimantNumber === this.props.claims[0].claimantNumber
          ),
        });
      },
      () => {
        /* if there is an error fetching available claimnumbers do nothing. 
           User can still create issue for originally selected claim */
      }
    );
  }

  onCreateIssueError = (error) => {
    this.setState({ error, snackbar: { open: true, message: error.message, severity: 'error' } });
  };

  onCreateIssueSuccess = () => {
    this.props.issuesChangedCallback();
    this.setState({ snackbar: { open: true, message: 'Beiðni hefur verið send!', severity: 'success' } });
  };

  handleSnackbarClose = () => {
    const { snackbar } = this.state;
    this.setState({ snackbar: { ...snackbar, open: false } });
    if (snackbar.severity === 'success') {
      this.props.closeCreateIssueDrawer();
    }
  };

  setIsSubmittingIssue = (isSubmitting) => {
    this.setState({ isSubmittingIssue: isSubmitting });
  };

  handleCreateIssue = (issue) => {
    return createIssue(issue, this.setIsSubmittingIssue, this.onCreateIssueSuccess, this.onCreateIssueError);
  };

  handleAddAttachments = (attachments, isValid) => {
    this.setState((prevState) => {
      return {
        message: {
          ...prevState.message,
          attachments: [...attachments],
        },
        areAttachmentsValid: isValid,
        error: null,
      };
    });
  };

  handleMessageChange = (event) => {
    if (event === undefined || event.target === undefined) {
      return;
    }
    const { value } = event.target;
    this.setState((prevState) => {
      return {
        error: null,
        message: { ...prevState.message, text: value },
      };
    });
  };

  handleTypeChange = (event) => {
    if (event === undefined || event.target === undefined) {
      return;
    }
    this.setState({ type: event.target.value });
  };

  handleConfirm = () => {
    const { type, message, isPersonalInfoRequest, claimIds, paymentAmount, monthlyCapacity, installments } = this.state;
    this.setState({ error: null });

    // For payment arrangement requests, prepend the structured fields to the message body
    // as key=value pairs so the Front conversation is easy to read.
    let messageText = message.text;
    if (type === issueType.PayerAgreementRequest) {
      const lines = [];
      if (paymentAmount) lines.push(`Upphæð innborgunar: ${paymentAmount}`);
      lines.push(`Mánaðarleg greiðslugeta: ${monthlyCapacity}`);
      lines.push(`Fjöldi mánaðarlegra afborgana: ${installments}`);
      if (message.text) lines.push(`Skilaboð: ${message.text}`);
      messageText = lines.join('\n');
    }

    return this.handleCreateIssue({
      type,
      claimIds: isPersonalInfoRequest ? [] : claimIds,
      message: { ...message, text: messageText },
    });
  };

  handleChangeClaimIds = (claimIds) => {
    this.setState({
      claimIds,
    });
  };

  handleAgreementFieldChange = (field) => (event) => {
    this.setState({ [field]: event.target.value, error: null });
  };

  renderHeader = () => {
    return (
      <>
        <Typography key="headline" variant="h1">
          {this.state.isPersonalInfoRequest
            ? 'Umsókn um eigin persónuupplýsingar'
            : this.state.type === issueType.PayerAgreementRequest
            ? 'Beiðni um greiðslusamkomulag'
            : 'Ný samskipti'}
        </Typography>
        {!this.state.hasContactInfo && (
          <Box sx={{ pl: '30px' }}>
            <Typography sx={{ fontSize: '12px' }}>
              Ef þú skráir tengiliðsupplýsingar þínar undir{' '}
              <Link
                href="/stillingar/"
                style={{
                  marginTop: '-3px',
                  marginBottom: '-3px',
                  display: 'inline-block',
                  '@media print': {
                    paddingLeft: '0px',
                  },
                  padding: '3px',
                }}
              >
                stillingar
              </Link>{' '}
              geturðu fengið tilkynningu í tölvupósti eða SMS smáskilaboðum þegar þú færð svar við þessari fyrirspurn.
            </Typography>
          </Box>
        )}
      </>
    );
  };

  renderClaims = () => {
    if (this.state.isPersonalInfoRequest || this.state.availableClaims.length === 0) return <></>;

    return (
      <MultiSelectClaims
        availableClaims={this.state.availableClaims}
        selectedClaimIds={this.state.claimIds}
        handleChangeClaimIds={this.handleChangeClaimIds}
      />
    );
  };

  renderIssueTypes = () => {
    const isAgreementRequest = this.state.type === issueType.PayerAgreementRequest;
    return this.state.isPersonalInfoRequest || isAgreementRequest ? null : (
      <Box sx={{ p: '15px 30px' }}>
        <FormControl fullWidth>
          <InputLabel id="select-issueType-label">Gerð samskipta</InputLabel>
          <Select
            id="select-issueType"
            labelId="select-issueType-label"
            value={this.state.type}
            onChange={this.handleTypeChange}
            input={<OutlinedInput label="Gerð samskipta" />}
            renderValue={(value) => {
              return <Typography sx={{ m: '5px 15px' }}>{getDescription(value)}</Typography>;
            }}
          >
            {issueTypes(this.state.claimIds).map((item, i) => {
              return (
                <MenuItem key={i} value={item}>
                  {getDescription(item)}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>
    );
  };

  renderPayerAgreementInstructions = () => {
    if (this.state.type !== issueType.PayerAgreementRequest) {
      return null;
    }
    return (
      <Box sx={{ p: '15px 35px', borderRadius: '4px' }}>
        <Typography sx={{ fontSize: '16px' }}>
          Fyrir mál á lögheimtustigi þarf starfsmaður Motus að samþykkja greiðslusamkomulag og áætla greiðslur í samræmi
          við skilyrði kröfuhafa. Vinsamlega taktu fram greiðslugetu og mögulega innborgun á mál ef það á við
        </Typography>
      </Box>
    );
  };

  // Renders structured payment arrangement fields for L2 (PayerAgreementRequest) claims.
  renderPayerAgreementFields = () => {
    if (this.state.type !== issueType.PayerAgreementRequest) return null;

    const { paymentAmount, monthlyCapacity, installments } = this.state;

    return (
      <Box sx={{ p: '0px 30px 15px' }}>
        <TextField
          label="Upphæð innborgunar (kr.)"
          fullWidth
          sx={{ mb: '12px' }}
          type="text"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 12 }}
          onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }}
          value={paymentAmount}
          onChange={(e) => this.setState({ paymentAmount: e.target.value.replace(/\D/g, '').slice(0, 12), error: null })}
        />
        <TextField
          label="Mánaðarleg greiðslugeta (kr.)"
          fullWidth
          sx={{ mb: '12px' }}
          type="text"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 12 }}
          onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }}
          required
          value={monthlyCapacity}
          onChange={(e) => this.setState({ monthlyCapacity: e.target.value.replace(/\D/g, '').slice(0, 12), error: null })}
        />
        <TextField
          label="Fjöldi mánaðarlegra afborgana"
          fullWidth
          sx={{ mb: '4px' }}
          type="text"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 3 }}
          onKeyDown={(e) => { if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault(); }}
          required
          value={installments}
          onChange={(e) => this.setState({ installments: e.target.value.replace(/\D/g, '').slice(0, 3), error: null })}
        />
      </Box>
    );
  };

  render() {
    const { isSubmittingIssue, areAttachmentsValid, message, type, monthlyCapacity, installments } = this.state;
    const { closeCreateIssueDrawer } = this.props;
    const isAgreementRequest = type === issueType.PayerAgreementRequest;

    // For agreement requests, monthly capacity and installments are required;
    // the free-text message is optional since the structured fields carry the key info.
    const areAgreementFieldsValid = !isAgreementRequest || (
      monthlyCapacity !== '' && Number(monthlyCapacity) >= 0 &&
      installments !== '' && Number.isInteger(Number(installments)) && Number(installments) >= 1
    );
    const isMessageValid = isAgreementRequest ? true : message.text.length > 0;

    return (
      <>
      <Drawer
        key="createNewIssueDrawer"
        anchor="right"
        open={true}
        onClose={closeCreateIssueDrawer}
        className={issueStyles.createIssueDrawer}
      >
        <Box>
          {this.renderHeader()}
          {this.renderClaims()}
          {this.renderIssueTypes()}
          {this.renderPayerAgreementInstructions()}
          {this.renderPayerAgreementFields()}

          <Box sx={{ p: '15px 30px' }}>
            <MessageForm
              canConfirm={
                (this.state.claimIds.length !== 0 || this.state.isPersonalInfoRequest) &&
                !isSubmittingIssue &&
                areAttachmentsValid &&
                type !== '' &&
                isMessageValid &&
                areAgreementFieldsValid
              }
              canAddAttachment={!this.state.isPersonalInfoRequest}
              message={message}
              handleAddAttachments={this.handleAddAttachments}
              handleMessageChange={this.handleMessageChange}
              handleConfirm={this.handleConfirm}
              handleClose={closeCreateIssueDrawer}
            />
          </Box>
        </Box>
      </Drawer>
      <Snackbar
        open={this.state.snackbar.open}
        autoHideDuration={4000}
        onClose={this.handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={this.state.snackbar.severity} onClose={this.handleSnackbarClose}>
          {this.state.snackbar.message}
        </Alert>
      </Snackbar>
      </>
    );
  }
}

export default CreateIssueSideBar;
